import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  MessageCircle,
  Navigation,
  Building2,
  Sparkles,
} from "lucide-react";
import Navbar from "../components/navigation/Navbar";
import Footer from "../components/Landing/Footer";

const ContactUs = () => {
  const contactInfo = [
    {
      icon: MapPin,
      title: "Visit Our Office",
      details: "71 Swaniker Street, Abelemkpe",
      subDetails: "Accra, Ghana",
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-50",
      iconColor: "text-blue-600",
    },
    {
      icon: Phone,
      title: "Call Us",
      details: "+233 596 921 080",
      subDetails: "Mon - Fri, 9:00 AM - 5:00 PM",
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-green-50",
      iconColor: "text-green-600",
    },
    {
      icon: Mail,
      title: "Email Us",
      details: "support@dealboxx.com",
      subDetails: "We'll respond within 24 hours",
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-purple-50",
      iconColor: "text-purple-600",
    },
  ];

  const features = [
    {
      icon: MessageCircle,
      title: "Quick Response",
      description: "Get answers to your questions within 24 hours",
    },
    {
      icon: Clock,
      title: "Business Hours",
      description: "Monday to Friday, 9:00 AM - 5:00 PM GMT",
    },
    {
      icon: Building2,
      title: "Professional Team",
      description: "Dedicated support staff ready to assist you",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF7F7F]/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-300/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-300/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container relative z-10 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-[#FF7F7F]/10 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-[#FF7F7F]" />
              <span className="text-sm font-semibold text-[#FF7F7F]">
                Get In Touch
              </span>
            </div>

            <h1 className="mb-6 text-4xl font-bold text-gray-900 md:text-5xl lg:text-6xl">
              Let's Start a{" "}
              <span className="bg-gradient-to-r from-[#FF7F7F] to-pink-600 bg-clip-text text-transparent">
                Conversation
              </span>
            </h1>

            <p className="max-w-3xl mx-auto text-lg text-gray-600 md:text-xl">
              Whether you have questions about affiliate partnerships, need
              support, or just want to say hello, we're here to help!
            </p>
          </motion.div>

          {/* Contact Cards */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 gap-6 mt-16 md:grid-cols-3"
          >
            {contactInfo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#FF7F7F] to-pink-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
                <div className="relative p-8 bg-white border border-gray-100 shadow-lg rounded-2xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                  <div
                    className={`inline-flex p-4 ${item.bgColor} rounded-2xl mb-4`}
                  >
                    <item.icon className={`w-8 h-8 ${item.iconColor}`} />
                  </div>

                  <h3 className="mb-3 text-xl font-bold text-gray-900">
                    {item.title}
                  </h3>

                  <p className="mb-1 text-lg font-semibold text-gray-800">
                    {item.details}
                  </p>

                  <p className="text-sm text-gray-500">{item.subDetails}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Map Section */}
      <section className="relative py-20 bg-white overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#FF7F7F]/5 to-pink-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-purple-500/5 to-blue-500/5 rounded-full blur-3xl"></div>

        <div className="container relative z-10 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              Find Us on the{" "}
              <span className="bg-gradient-to-r from-[#FF7F7F] to-pink-600 bg-clip-text text-transparent">
                Map
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600">
              Located in the heart of Abelemkpe, Accra. Drop by for a visit!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Outer decorative border */}
            <div className="absolute -inset-1 bg-gradient-to-r from-[#FF7F7F] via-pink-500 to-purple-600 rounded-3xl blur-lg opacity-30"></div>

            {/* Main container */}
            <div className="relative bg-white p-2 rounded-3xl shadow-2xl">
              {/* Address Bar */}
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white rounded-t-2xl border-b border-gray-100">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-r from-[#FF7F7F] to-pink-500 rounded-lg">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">
                        Our Location
                      </p>
                      <p className="text-base font-semibold text-gray-900">
                        71 Swaniker Street, Abelemkpe, Accra, Ghana
                      </p>
                    </div>
                  </div>

                  <motion.a
                    href="https://maps.app.goo.gl/nJtbVe99MLkiY6f4A"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#FF7F7F] to-pink-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Navigation className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                    <span className="font-semibold text-sm">
                      Get Directions
                    </span>
                  </motion.a>
                </div>
              </div>

              {/* Map Container */}
              <div className="relative h-[500px] md:h-[600px] bg-gray-100 rounded-b-2xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5949.355097013342!2d-0.2163131152349484!3d5.608575006902703!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xfdf9bd206596083%3A0xe11d6e6fca473da6!2sGWO%20SEVO%20Solutions!5e0!3m2!1sen!2sgh!4v1760611688238!5m2!1sen!2sgh"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full h-full"
                  title="DealBoxx Office Location - 71 Swaniker Street, Abelemkpe"
                ></iframe>

                {/* Corner decoration */}
                <div className="absolute top-0 left-0 w-20 h-20 bg-gradient-to-br from-[#FF7F7F]/20 to-transparent pointer-events-none"></div>
                <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-pink-500/20 to-transparent pointer-events-none"></div>
              </div>
            </div>

            {/* Floating info badges */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="hidden lg:block absolute -left-6 top-1/4 bg-white p-4 rounded-2xl shadow-xl border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Phone className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Call Us</p>
                  <p className="text-sm font-semibold text-gray-900">
                    +233 596 921 080
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="hidden lg:block absolute -right-6 bottom-1/4 bg-white p-4 rounded-2xl shadow-xl border border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Working Hours</p>
                  <p className="text-sm font-semibold text-gray-900">
                    Mon - Fri, 9AM - 5PM
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
              Why{" "}
              <span className="bg-gradient-to-r from-[#FF7F7F] to-pink-600 bg-clip-text text-transparent">
                Contact Us?
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-lg text-gray-600">
              We're committed to providing exceptional support to our affiliate
              partners
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-r from-[#FF7F7F] to-pink-500 rounded-2xl shadow-lg">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>

                <h3 className="mb-2 text-xl font-bold text-gray-900">
                  {feature.title}
                </h3>

                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-r from-[#FF7F7F] via-pink-500 to-purple-600">
        <div className="absolute inset-0 bg-black/10"></div>

        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container relative z-10 px-4 mx-auto text-center max-w-4xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="mb-6 text-3xl font-bold text-white md:text-4xl lg:text-5xl">
              Ready to Join Our Affiliate Network?
            </h2>

            <p className="mb-8 text-lg text-white/90 md:text-xl">
              Start earning commissions today by becoming a DealBoxx affiliate
              partner. It's quick, easy, and completely free!
            </p>

            <motion.a
              href="/register"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-semibold text-[#FF7F7F] bg-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started Now
              <Sparkles className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
